import { useEffect, useState } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, MessageSquare, Users, LogOut, Plus, Trash2, Edit3, X, Check, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/layout/Navbar";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editProject, setEditProject] = useState(null); // project object or {isNew:true}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user && user.role) void loadAll();
  }, [user]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, p, i, sub] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/projects"),
        api.get("/admin/inquiries"),
        api.get("/admin/subscribers"),
      ]);
      setStats(s.data);
      setProjects(p.data);
      setInquiries(i.data);
      setSubscribers(sub.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (user === null) return <div className="p-10 mono-label">Checking session…</div>;
  if (user === false) return <Navigate to="/admin/login" replace state={{ from: location }} />;

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex flex-col lg:flex-row" data-testid="admin-dashboard">
      {/* SIDEBAR */}
      <aside className="lg:w-60 bg-white lg:border-r border-zinc-200 shadow-sm lg:shadow-none flex flex-col lg:sticky lg:top-0 lg:h-screen z-20">
        <div className="p-4 lg:p-6 flex items-center gap-3 border-b border-zinc-200 flex-shrink-0">
          <LogoMark className="w-9 h-9" />
          <div>
            <div className="font-display text-lg leading-none">Mohr</div>
            <div className="mono-label text-[9px] mt-1">Admin</div>
          </div>
        </div>
        <nav className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible border-b lg:border-b-0 border-zinc-200">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "projects", label: "Projects", icon: FolderOpen },
            { id: "inquiries", label: "Inquiries", icon: MessageSquare },
            { id: "subscribers", label: "Subscribers", icon: Users },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2.5 px-4 lg:px-5 py-3 lg:py-3.5 text-xs font-medium whitespace-nowrap transition-colors ${
                tab === id ? "bg-[#0A0A0A] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto p-4 lg:p-5 border-t border-zinc-200 flex lg:flex-col gap-4 lg:gap-2.5 justify-between lg:justify-start">
          <Link to="/" className="mono-label text-zinc-500 hover:text-black inline-flex items-center gap-2" data-testid="admin-view-site">
            <ExternalLink className="w-3 h-3" /> View Site
          </Link>
          <button onClick={logout} className="mono-label text-red-700 hover:text-red-900 inline-flex items-center gap-2 text-left" data-testid="admin-logout">
            <LogOut className="w-3 h-3" /> Log out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 p-5 md:p-8 lg:p-10 min-w-0">
        {err && <div className="mb-6 text-red-700 text-sm">{err}</div>}

        {tab === "overview" && <Overview stats={stats} />}

        {tab === "projects" && (
          <ProjectsTab
            projects={projects}
            onCreate={() => setEditProject({ isNew: true })}
            onEdit={(p) => setEditProject(p)}
            onDelete={async (id) => {
              if (!window.confirm("Delete this project?")) return;
              await api.delete(`/admin/projects/${id}`);
              loadAll();
            }}
          />
        )}

        {tab === "inquiries" && (
          <InquiriesTab
            inquiries={inquiries}
            onUpdate={async (id, status) => {
              await api.patch(`/admin/inquiries/${id}`, { status });
              loadAll();
            }}
            onDelete={async (id) => {
              if (!window.confirm("Delete this inquiry?")) return;
              await api.delete(`/admin/inquiries/${id}`);
              loadAll();
            }}
          />
        )}

        {tab === "subscribers" && <SubscribersTab subscribers={subscribers} />}
      </main>

      {editProject && (
        <ProjectEditor
          project={editProject.isNew ? null : editProject}
          onClose={() => setEditProject(null)}
          onSaved={() => {
            setEditProject(null);
            loadAll();
          }}
        />
      )}
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <div className="mono-label">Loading…</div>;
  const cards = [
    { k: "Total Projects", v: stats.projects, sub: `${stats.published_projects} published` },
    { k: "Inquiries", v: stats.inquiries, sub: `${stats.new_inquiries} new` },
    { k: "Subscribers", v: stats.subscribers, sub: "newsletter" },
  ];
  return (
    <div>
      <div className="mono-label text-zinc-500">Overview</div>
      <h1 className="font-display text-5xl md:text-6xl mt-4 leading-tight tracking-tight">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 mt-10">
        {cards.map((c) => (
          <div key={c.k} className="bg-white p-8">
            <div className="mono-label text-zinc-500">{c.k}</div>
            <div className="font-display text-6xl mt-4 font-light">{c.v}</div>
            <div className="text-zinc-500 text-sm mt-2">{c.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectsTab({ projects, onCreate, onEdit, onDelete }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="mono-label text-zinc-500">Projects</div>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight tracking-tight">
            {projects.length} total
          </h1>
        </div>
        <button onClick={onCreate} className="btn-primary" data-testid="admin-new-project">
          <Plus className="w-4 h-4" /> New Project
        </button>
      </div>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Title</Th>
              <Th>Client</Th>
              <Th>Year</Th>
              <Th>Category</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`admin-project-row-${p.slug}`}>
                <Td>
                  <div className="font-medium">{p.title}</div>
                  <div className="mono-label text-zinc-500 mt-1">{p.slug}</div>
                </Td>
                <Td>{p.client}</Td>
                <Td>{p.year}</Td>
                <Td>{p.category}</Td>
                <Td>
                  <span className={`mono-label ${p.published ? "text-emerald-700" : "text-zinc-500"}`}>
                    {p.published ? "● Published" : "○ Draft"}
                  </span>
                </Td>
                <Td>
                  <div className="flex items-center gap-3">
                    <button onClick={() => onEdit(p)} className="text-zinc-600 hover:text-black" title="Edit" data-testid={`admin-edit-${p.slug}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(p.id)} className="text-zinc-600 hover:text-red-700" title="Delete" data-testid={`admin-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center mono-label text-zinc-500">No projects yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InquiriesTab({ inquiries, onUpdate, onDelete }) {
  return (
    <div>
      <div className="mono-label text-zinc-500">Inquiries</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight tracking-tight mb-8">
        {inquiries.length} received
      </h1>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Date</Th><Th>Name</Th><Th>Contact</Th><Th>Subject</Th><Th>Message</Th><Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((i) => (
              <tr key={i.id} className="border-b border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`admin-inquiry-row-${i.id}`}>
                <Td className="whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</Td>
                <Td>{i.name}</Td>
                <Td>
                  <div>{i.email}</div>
                  {i.phone && <div className="text-zinc-500 text-xs mt-1">{i.phone}</div>}
                </Td>
                <Td>{i.subject || "—"}</Td>
                <Td className="max-w-xs">
                  <div className="whitespace-pre-wrap line-clamp-3">{i.message}</div>
                </Td>
                <Td>
                  <select
                    value={i.status}
                    onChange={(e) => onUpdate(i.id, e.target.value)}
                    className="border border-zinc-300 px-2 py-1 text-xs bg-white"
                    data-testid={`admin-inquiry-status-${i.id}`}
                  >
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="archived">Archived</option>
                  </select>
                </Td>
                <Td>
                  <button onClick={() => onDelete(i.id)} className="text-zinc-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </Td>
              </tr>
            ))}
            {inquiries.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center mono-label text-zinc-500">No inquiries yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubscribersTab({ subscribers }) {
  return (
    <div>
      <div className="mono-label text-zinc-500">Newsletter</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 leading-tight tracking-tight mb-8">
        {subscribers.length} subscribers
      </h1>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr><Th>Email</Th><Th>Subscribed</Th></tr>
          </thead>
          <tbody>
            {subscribers.map((s) => (
              <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                <Td>{s.email}</Td>
                <Td>{new Date(s.created_at).toLocaleString()}</Td>
              </tr>
            ))}
            {subscribers.length === 0 && (
              <tr><td colSpan={2} className="p-10 text-center mono-label text-zinc-500">No subscribers yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function ProjectEditor({ project, onClose, onSaved }) {
  const [form, setForm] = useState(
    project || {
      title: "", slug: "", client: "", location: "", year: "", category: "Residential",
      scope: "", description: "", cover_image: "", gallery: [], featured: false, published: true,
    }
  );
  const [galleryInput, setGalleryInput] = useState(form.gallery?.join("\n") || "");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    const body = {
      ...form,
      slug: form.slug || slugify(form.title),
      gallery: galleryInput.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (project && project.id) {
        await api.put(`/admin/projects/${project.id}`, body);
      } else {
        await api.post("/admin/projects", body);
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-3xl my-10 border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-project-editor"
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-zinc-200">
          <h3 className="font-display text-2xl">{project ? "Edit Project" : "New Project"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-8 space-y-5">
          <Row label="Title" required>
            <input required className="admin-input" value={form.title} onChange={update("title")} data-testid="project-input-title" />
          </Row>
          <Row label="Slug (auto from title if empty)">
            <input className="admin-input" value={form.slug} onChange={update("slug")} placeholder={slugify(form.title) || "project-slug"} data-testid="project-input-slug" />
          </Row>
          <div className="grid md:grid-cols-2 gap-5">
            <Row label="Client"><input className="admin-input" value={form.client} onChange={update("client")} /></Row>
            <Row label="Location"><input className="admin-input" value={form.location} onChange={update("location")} /></Row>
            <Row label="Year"><input className="admin-input" value={form.year} onChange={update("year")} /></Row>
            <Row label="Category">
              <select className="admin-input" value={form.category} onChange={update("category")}>
                <option>Residential</option>
                <option>Institutional</option>
                <option>Infrastructure</option>
                <option>Commercial</option>
                <option>Industrial</option>
              </select>
            </Row>
          </div>
          <Row label="Scope"><input className="admin-input" value={form.scope} onChange={update("scope")} /></Row>
          <Row label="Description">
            <textarea rows={4} className="admin-input resize-none" value={form.description} onChange={update("description")} />
          </Row>
          <Row label="Cover Image URL">
            <input className="admin-input" value={form.cover_image} onChange={update("cover_image")} placeholder="https://…" />
          </Row>
          <Row label="Gallery URLs (one per line)">
            <textarea rows={4} className="admin-input resize-none font-mono text-xs" value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} placeholder="https://…" />
          </Row>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.published} onChange={update("published")} /> Published
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={update("featured")} /> Featured on home
            </label>
          </div>
          {err && <div className="text-red-700 text-sm">{err}</div>}
          <div className="flex items-center justify-end gap-4 pt-2">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="project-editor-cancel">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="project-editor-save">
              <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .admin-input {
          width: 100%;
          background: #fff;
          border: 1px solid #E4E4E7;
          padding: 10px 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
          font-family: Manrope, system-ui, sans-serif;
        }
        .admin-input:focus { border-color: #0A0A0A; }
      `}</style>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <label className="block">
      <span className="mono-label text-zinc-500">{label}{required && <span className="text-[#BE1E2D] ml-1">*</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function Th({ children }) { return <th className="text-left px-6 py-3 mono-label text-zinc-500">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-6 py-4 ${className}`}>{children}</td>; }
